/**
 * Exam Office Admin Page - question bank, exam paper moderation, marks
 * capture by component, result publication controls, remark requests,
 * and malpractice cases. Sits alongside the existing ExamSchedulingPage
 * (sessions/schedules/seating/invigilators) rather than replacing it —
 * this page covers everything routers/exam_papers.py, exam_marks.py,
 * exam_remarks.py and exam_malpractice.py add on top of that.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import examsService from '../services/examsService';
import examOfficeService from '../services/examOfficeService';
import classesService from '../services/classesService';
import { CircleNotch } from '@phosphor-icons/react';
import './ExamOfficePage.css';

const TABS = [
  { key: 'papers', label: 'Question Bank & Papers' },
  { key: 'marks', label: 'Marks & Components' },
  { key: 'publication', label: 'Result Publication' },
  { key: 'remarks', label: 'Remark Requests' },
  { key: 'malpractice', label: 'Malpractice Cases' },
];

const STATUS_COLORS = {
  draft: '#6b7280', pending: '#6b7280', reported: '#6b7280',
  submitted: '#3b82f6', under_review: '#3b82f6', under_investigation: '#3b82f6',
  approved: '#10b981', upheld: '#10b981', revised: '#10b981', resolved: '#10b981', published: '#10b981',
  rejected: '#ef4444', dismissed: '#ef4444', cancelled: '#9ca3af',
};

function statusColor(status) {
  return STATUS_COLORS[status] || '#6b7280';
}

function StatusBadge({ status }) {
  if (!status) return <span>—</span>;
  return <span className="badge" style={{ backgroundColor: statusColor(status) }}>{status.replace(/_/g, ' ').toUpperCase()}</span>;
}

function ExamOfficePage() {
  const [tab, setTab] = useState('papers');
  const [subjects, setSubjects] = useState([]);
  const [sessions, setSessions] = useState([]);

  useEffect(() => {
    classesService.listAllSubjects().then((data) => setSubjects(data || [])).catch(() => {});
    examsService.listSessions().then((data) => setSessions(data || [])).catch(() => {});
  }, []);

  const subjectName = (id) => subjects.find((s) => s.id === id)?.name || id;

  return (
    <Layout>
      <div className="page exam-office-page">
        <div className="page-header">
          <div>
            <h1>Exam Office</h1>
            <p>Question bank, paper moderation, marks by component, result publication, remark requests and malpractice cases</p>
          </div>
        </div>

        <div className="eop-tabs">
          {TABS.map((t) => (
            <button key={t.key} type="button" className={tab === t.key ? 'btn btn-primary' : 'btn btn-secondary'} onClick={() => setTab(t.key)}>
              {t.label}
            </button>
          ))}
        </div>

        {tab === 'papers' && <PapersTab subjects={subjects} subjectName={subjectName} sessions={sessions} />}
        {tab === 'marks' && <MarksTab sessions={sessions} subjectName={subjectName} />}
        {tab === 'publication' && <PublicationTab sessions={sessions} setSessions={setSessions} />}
        {tab === 'remarks' && <RemarksTab />}
        {tab === 'malpractice' && <MalpracticeTab sessions={sessions} />}
      </div>
    </Layout>
  );
}

/* ---------------------------------------------------------------- */
/* Shared: session -> schedule picker                                 */
/* ---------------------------------------------------------------- */

function useScheduleOptions(sessions) {
  const [sessionId, setSessionId] = useState('');
  const [schedules, setSchedules] = useState([]);
  const [scheduleId, setScheduleId] = useState('');

  const pickSession = async (id) => {
    setSessionId(id);
    setScheduleId('');
    setSchedules([]);
    if (!id) return;
    try {
      const detail = await examsService.getSession(id);
      setSchedules(detail.schedules || []);
    } catch (error) {
      toast.error('Failed to load exam schedules: ' + error.message);
    }
  };

  return { sessionId, pickSession, schedules, scheduleId, setScheduleId };
}

function SessionScheduleSelect({ sessions, picker, subjectName }) {
  return (
    <div className="eop-picker-row">
      <div className="form-group">
        <label>Exam Session</label>
        <select value={picker.sessionId} onChange={(e) => picker.pickSession(e.target.value)}>
          <option value="">Select session…</option>
          {sessions.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      </div>
      <div className="form-group">
        <label>Schedule (Class / Subject)</label>
        <select value={picker.scheduleId} onChange={(e) => picker.setScheduleId(e.target.value)} disabled={!picker.sessionId}>
          <option value="">Select schedule…</option>
          {picker.schedules.map((s) => (
            <option key={s.id} value={s.id}>{s.exam_date} — {subjectName(s.subject_id)} ({s.class_id ? s.class_id.slice(0, 8) : ''})</option>
          ))}
        </select>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- */
/* Question Bank & Papers                                             */
/* ---------------------------------------------------------------- */

function PapersTab({ subjects, subjectName, sessions }) {
  const [view, setView] = useState('papers');
  return (
    <div className="eop-panel">
      <div className="eop-subtabs">
        <button type="button" className={view === 'papers' ? 'btn btn-primary' : 'btn btn-secondary'} onClick={() => setView('papers')}>Exam Papers</button>
        <button type="button" className={view === 'bank' ? 'btn btn-primary' : 'btn btn-secondary'} onClick={() => setView('bank')}>Question Bank</button>
      </div>
      {view === 'papers' ? <ExamPapersPanel subjects={subjects} subjectName={subjectName} sessions={sessions} /> : <QuestionBankPanel subjects={subjects} />}
    </div>
  );
}

function QuestionBankPanel({ subjects }) {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subjectFilter, setSubjectFilter] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ subject_id: '', topic: '', question_type: 'short_answer', question_text: '', marks: 1, difficulty: 'medium', answer_key: '' });
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await examOfficeService.listQuestions(subjectFilter ? { subject_id: subjectFilter } : {});
      setQuestions(data || []);
    } catch (error) {
      toast.error('Failed to load question bank: ' + error.message);
    } finally {
      setLoading(false);
    }
  }, [subjectFilter]);

  useEffect(() => { load(); }, [load]);

  const subjectName = (id) => subjects.find((s) => s.id === id)?.name || id;

  const submitCreate = async (e) => {
    e.preventDefault();
    if (!form.subject_id || !form.question_text.trim()) {
      toast.error('Subject and question text are required');
      return;
    }
    setSubmitting(true);
    try {
      await examOfficeService.createQuestion(form);
      toast.success('Question added to bank');
      setShowCreate(false);
      setForm({ subject_id: '', topic: '', question_type: 'short_answer', question_text: '', marks: 1, difficulty: 'medium', answer_key: '' });
      load();
    } catch (error) {
      toast.error('Failed to add question: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (id) => {
    try {
      await examOfficeService.deleteQuestion(id);
      toast.success('Question deleted');
      load();
    } catch (error) {
      toast.error('Failed to delete question: ' + error.message);
    }
  };

  return (
    <div>
      <div className="filters">
        <select className="filter-select" value={subjectFilter} onChange={(e) => setSubjectFilter(e.target.value)}>
          <option value="">All Subjects</option>
          {subjects.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <button className="btn btn-primary" onClick={() => setShowCreate(true)}>Add Question</button>
      </div>

      {loading ? (
        <div className="loading"><CircleNotch size={28} weight="bold" /> Loading...</div>
      ) : questions.length === 0 ? (
        <div className="empty-state"><p>No questions in the bank yet</p></div>
      ) : (
        <div className="eop-table">
          <table>
            <thead><tr><th>Subject</th><th>Topic</th><th>Type</th><th>Question</th><th>Marks</th><th>Difficulty</th><th>Actions</th></tr></thead>
            <tbody>
              {questions.map((q) => (
                <tr key={q.id}>
                  <td>{subjectName(q.subject_id)}</td>
                  <td>{q.topic || '—'}</td>
                  <td>{q.question_type.replace('_', ' ')}</td>
                  <td className="eop-truncate">{q.question_text}</td>
                  <td>{q.marks}</td>
                  <td>{q.difficulty}</td>
                  <td><button className="btn btn-sm btn-danger" onClick={() => remove(q.id)}>Delete</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showCreate && (
        <div className="modal-overlay" onClick={() => setShowCreate(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header"><h2>Add Question</h2><button className="btn-close" onClick={() => setShowCreate(false)}>×</button></div>
            <form onSubmit={submitCreate}>
              <div className="form-group">
                <label>Subject *</label>
                <select value={form.subject_id} onChange={(e) => setForm({ ...form, subject_id: e.target.value })} required>
                  <option value="">Select subject…</option>
                  {subjects.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div className="form-group"><label>Topic</label><input value={form.topic} onChange={(e) => setForm({ ...form, topic: e.target.value })} /></div>
              <div className="form-group">
                <label>Question Type</label>
                <select value={form.question_type} onChange={(e) => setForm({ ...form, question_type: e.target.value })}>
                  <option value="mcq">Multiple Choice</option>
                  <option value="short_answer">Short Answer</option>
                  <option value="essay">Essay</option>
                  <option value="practical">Practical</option>
                </select>
              </div>
              <div className="form-group"><label>Question Text *</label><textarea rows="3" value={form.question_text} onChange={(e) => setForm({ ...form, question_text: e.target.value })} required /></div>
              <div className="form-group"><label>Answer Key (marking reference, not shown to students)</label><textarea rows="2" value={form.answer_key} onChange={(e) => setForm({ ...form, answer_key: e.target.value })} /></div>
              <div className="eop-picker-row">
                <div className="form-group"><label>Marks</label><input type="number" min="0" step="0.5" value={form.marks} onChange={(e) => setForm({ ...form, marks: parseFloat(e.target.value) || 0 })} /></div>
                <div className="form-group">
                  <label>Difficulty</label>
                  <select value={form.difficulty} onChange={(e) => setForm({ ...form, difficulty: e.target.value })}>
                    <option value="easy">Easy</option><option value="medium">Medium</option><option value="hard">Hard</option>
                  </select>
                </div>
              </div>
              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowCreate(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={submitting}>{submitting ? 'Saving...' : 'Add Question'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function ExamPapersPanel({ subjects, subjectName, sessions }) {
  const [papers, setPapers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ subject_id: '', title: '', instructions: '', duration_minutes: 60 });
  const picker = useScheduleOptions(sessions);
  const [submitting, setSubmitting] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [bankQuestions, setBankQuestions] = useState([]);
  const [addQuestionId, setAddQuestionId] = useState('');
  const [addMarks, setAddMarks] = useState(5);
  const [moderationNotes, setModerationNotes] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await examOfficeService.listPapers(statusFilter ? { status_filter: statusFilter } : {});
      setPapers(data || []);
    } catch (error) {
      toast.error('Failed to load exam papers: ' + error.message);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const submitCreate = async (e) => {
    e.preventDefault();
    if (!form.subject_id || !form.title.trim()) {
      toast.error('Subject and title are required');
      return;
    }
    setSubmitting(true);
    try {
      await examOfficeService.createPaper({ ...form, exam_schedule_id: picker.scheduleId || null });
      toast.success('Exam paper created (draft)');
      setShowCreate(false);
      setForm({ subject_id: '', title: '', instructions: '', duration_minutes: 60 });
      load();
    } catch (error) {
      toast.error('Failed to create paper: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const openDetail = async (paper) => {
    setDetailLoading(true);
    setModerationNotes('');
    try {
      const data = await examOfficeService.getPaper(paper.id);
      setDetail(data);
      const bank = await examOfficeService.listQuestions({ subject_id: data.subject_id });
      setBankQuestions(bank || []);
    } catch (error) {
      toast.error('Failed to load paper: ' + error.message);
    } finally {
      setDetailLoading(false);
    }
  };

  const refreshDetail = async () => {
    const data = await examOfficeService.getPaper(detail.id);
    setDetail(data);
  };

  const addQuestion = async () => {
    if (!addQuestionId) { toast.error('Select a question'); return; }
    try {
      await examOfficeService.addQuestionToPaper(detail.id, { question_bank_item_id: addQuestionId, marks_allocated: addMarks, order_index: (detail.questions?.length || 0) + 1 });
      toast.success('Question added to paper');
      setAddQuestionId('');
      await refreshDetail();
      load();
    } catch (error) {
      toast.error('Failed to add question: ' + error.message);
    }
  };

  const removeQuestion = async (linkId) => {
    try {
      await examOfficeService.removeQuestionFromPaper(detail.id, linkId);
      await refreshDetail();
      load();
    } catch (error) {
      toast.error('Failed to remove question: ' + error.message);
    }
  };

  const submitPaper = async () => {
    try {
      await examOfficeService.submitPaper(detail.id);
      toast.success('Paper submitted for moderation');
      await refreshDetail();
      load();
    } catch (error) {
      toast.error('Failed to submit paper: ' + error.message);
    }
  };

  const decide = async (approve) => {
    try {
      if (approve) await examOfficeService.approvePaper(detail.id, { notes: moderationNotes });
      else await examOfficeService.rejectPaper(detail.id, { notes: moderationNotes });
      toast.success(approve ? 'Paper approved' : 'Paper rejected');
      await refreshDetail();
      load();
    } catch (error) {
      toast.error('Failed to record decision: ' + error.message);
    }
  };

  return (
    <div>
      <div className="filters">
        <select className="filter-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="">All Statuses</option>
          <option value="draft">Draft</option><option value="submitted">Submitted</option>
          <option value="approved">Approved</option><option value="rejected">Rejected</option>
        </select>
        <button className="btn btn-primary" onClick={() => setShowCreate(true)}>Create Paper</button>
      </div>

      {loading ? (
        <div className="loading"><CircleNotch size={28} weight="bold" /> Loading...</div>
      ) : papers.length === 0 ? (
        <div className="empty-state"><p>No exam papers yet</p></div>
      ) : (
        <div className="eop-table">
          <table>
            <thead><tr><th>Title</th><th>Subject</th><th>Total Marks</th><th>Status</th><th>Actions</th></tr></thead>
            <tbody>
              {papers.map((p) => (
                <tr key={p.id}>
                  <td>{p.title}</td>
                  <td>{subjectName(p.subject_id)}</td>
                  <td>{p.total_marks}</td>
                  <td><StatusBadge status={p.status} /></td>
                  <td><button className="btn btn-sm btn-secondary" onClick={() => openDetail(p)}>Manage</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showCreate && (
        <div className="modal-overlay" onClick={() => setShowCreate(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header"><h2>Create Exam Paper</h2><button className="btn-close" onClick={() => setShowCreate(false)}>×</button></div>
            <form onSubmit={submitCreate}>
              <div className="form-group">
                <label>Subject *</label>
                <select value={form.subject_id} onChange={(e) => setForm({ ...form, subject_id: e.target.value })} required>
                  <option value="">Select subject…</option>
                  {subjects.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div className="form-group"><label>Title *</label><input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required /></div>
              <div className="form-group"><label>Instructions</label><textarea rows="2" value={form.instructions} onChange={(e) => setForm({ ...form, instructions: e.target.value })} /></div>
              <div className="form-group"><label>Duration (minutes)</label><input type="number" min="0" value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: parseInt(e.target.value, 10) || 0 })} /></div>
              <p className="eop-hint">Optionally link to a scheduled sitting:</p>
              <SessionScheduleSelect sessions={sessions} picker={picker} subjectName={subjectName} />
              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowCreate(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={submitting}>{submitting ? 'Creating...' : 'Create'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {(detail || detailLoading) && (
        <div className="modal-overlay" onClick={() => setDetail(null)}>
          <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
            {detailLoading || !detail ? <div className="loading"><CircleNotch size={28} weight="bold" /> Loading...</div> : (
              <>
                <div className="modal-header"><h2>{detail.title}</h2><button className="btn-close" onClick={() => setDetail(null)}>×</button></div>
                <div className="ticket-info-grid">
                  <div><strong>Subject:</strong><span>{subjectName(detail.subject_id)}</span></div>
                  <div><strong>Status:</strong><StatusBadge status={detail.status} /></div>
                  <div><strong>Total Marks:</strong><span>{detail.total_marks}</span></div>
                  <div><strong>Duration:</strong><span>{detail.duration_minutes || '—'} min</span></div>
                </div>

                <div className="section">
                  <h3>Questions</h3>
                  {detail.questions?.length ? (
                    <div className="eop-table">
                      <table>
                        <thead><tr><th>Question</th><th>Marks</th><th></th></tr></thead>
                        <tbody>
                          {detail.questions.map((q) => (
                            <tr key={q.id}>
                              <td className="eop-truncate">{q.question_text}</td>
                              <td>{q.marks_allocated}</td>
                              <td>{(detail.status === 'draft' || detail.status === 'rejected') && <button className="btn btn-sm btn-danger" onClick={() => removeQuestion(q.id)}>Remove</button>}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : <p className="empty">No questions added yet</p>}

                  {(detail.status === 'draft' || detail.status === 'rejected') && (
                    <div className="eop-picker-row">
                      <div className="form-group">
                        <label>Add from question bank</label>
                        <select value={addQuestionId} onChange={(e) => setAddQuestionId(e.target.value)}>
                          <option value="">Select question…</option>
                          {bankQuestions.map((q) => <option key={q.id} value={q.id}>{q.question_text.slice(0, 60)}</option>)}
                        </select>
                      </div>
                      <div className="form-group"><label>Marks</label><input type="number" min="0" step="0.5" value={addMarks} onChange={(e) => setAddMarks(parseFloat(e.target.value) || 0)} /></div>
                      <button className="btn btn-secondary" onClick={addQuestion}>Add</button>
                    </div>
                  )}
                </div>

                {(detail.status === 'draft' || detail.status === 'rejected') && (
                  <div className="section">
                    <button className="btn btn-primary" onClick={submitPaper} disabled={!detail.questions?.length && !detail.file_url}>Submit for Moderation</button>
                    {!detail.questions?.length && !detail.file_url && <p className="eop-hint">Add at least one question (or set a file_url) before submitting.</p>}
                  </div>
                )}

                {detail.status === 'submitted' && (
                  <div className="section actions">
                    <h3>Moderation Decision</h3>
                    <div className="form-group"><label>Notes</label><textarea rows="2" value={moderationNotes} onChange={(e) => setModerationNotes(e.target.value)} /></div>
                    <div className="eop-row-actions">
                      <button className="btn btn-success" onClick={() => decide(true)}>Approve</button>
                      <button className="btn btn-danger" onClick={() => decide(false)}>Reject</button>
                    </div>
                  </div>
                )}

                {detail.moderation_notes && (
                  <div className="section resolution">
                    <h3>Last Moderation Note</h3>
                    <p>{detail.moderation_notes}</p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------------------------------------------------------------- */
/* Marks & Components                                                 */
/* ---------------------------------------------------------------- */

function MarksTab({ sessions, subjectName }) {
  const picker = useScheduleOptions(sessions);
  const [components, setComponents] = useState([]);
  const [componentId, setComponentId] = useState('');
  const [showCreateComponent, setShowCreateComponent] = useState(false);
  const [componentForm, setComponentForm] = useState({ name: '', max_marks: 100, weight: 1 });
  const [students, setStudents] = useState([]);
  const [marksDraft, setMarksDraft] = useState({});
  const [loadingRoster, setLoadingRoster] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadComponents = useCallback(async () => {
    if (!picker.scheduleId) { setComponents([]); setComponentId(''); return; }
    try {
      const data = await examOfficeService.listComponents(picker.scheduleId);
      setComponents(data || []);
    } catch (error) {
      toast.error('Failed to load components: ' + error.message);
    }
  }, [picker.scheduleId]);

  useEffect(() => { loadComponents(); }, [loadComponents]);

  const createComponent = async (e) => {
    e.preventDefault();
    if (!componentForm.name.trim()) { toast.error('Component name is required'); return; }
    try {
      await examOfficeService.createComponent(picker.scheduleId, componentForm);
      toast.success('Component created');
      setShowCreateComponent(false);
      setComponentForm({ name: '', max_marks: 100, weight: 1 });
      loadComponents();
    } catch (error) {
      toast.error('Failed to create component: ' + error.message);
    }
  };

  const loadRoster = useCallback(async () => {
    if (!componentId) return;
    const schedule = picker.schedules.find((s) => s.id === picker.scheduleId);
    if (!schedule) return;
    setLoadingRoster(true);
    try {
      const [rosterResp, marksResp] = await Promise.all([
        classesService.getClassStudents(schedule.class_id),
        examOfficeService.listComponentMarks(componentId),
      ]);
      const roster = rosterResp.items || rosterResp || [];
      setStudents(roster);
      const draft = {};
      roster.forEach((s) => { draft[s.id] = ''; });
      (marksResp || []).forEach((m) => { draft[m.student_id] = String(m.score); });
      setMarksDraft(draft);
    } catch (error) {
      toast.error('Failed to load class roster: ' + error.message);
    } finally {
      setLoadingRoster(false);
    }
  }, [componentId, picker.schedules, picker.scheduleId]);

  useEffect(() => { loadRoster(); }, [loadRoster]);

  const saveMarks = async () => {
    const entries = Object.entries(marksDraft)
      .filter(([, v]) => v !== '' && v !== null && v !== undefined)
      .map(([student_id, v]) => ({ student_id, score: parseFloat(v) }));
    if (entries.length === 0) { toast.error('Enter at least one score'); return; }
    setSaving(true);
    try {
      const result = await examOfficeService.upsertMarks(componentId, entries);
      toast.success(`Saved (${result.created} new, ${result.updated} updated)`);
      loadRoster();
    } catch (error) {
      toast.error('Failed to save marks: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const component = components.find((c) => c.id === componentId);

  return (
    <div className="eop-panel">
      <SessionScheduleSelect sessions={sessions} picker={picker} subjectName={subjectName} />

      {picker.scheduleId && (
        <div className="eop-picker-row">
          <div className="form-group">
            <label>Component</label>
            <select value={componentId} onChange={(e) => setComponentId(e.target.value)}>
              <option value="">Select component…</option>
              {components.map((c) => <option key={c.id} value={c.id}>{c.name} (max {c.max_marks})</option>)}
            </select>
          </div>
          <button className="btn btn-secondary" onClick={() => setShowCreateComponent(true)}>New Component</button>
        </div>
      )}

      {componentId && (
        loadingRoster ? (
          <div className="loading"><CircleNotch size={28} weight="bold" /> Loading roster...</div>
        ) : students.length === 0 ? (
          <div className="empty-state"><p>No active students in this class</p></div>
        ) : (
          <div className="eop-table">
            <table>
              <thead><tr><th>Student</th><th>Score (max {component?.max_marks})</th></tr></thead>
              <tbody>
                {students.map((s) => (
                  <tr key={s.id}>
                    <td>{s.full_name || `${s.first_name} ${s.last_name}`}</td>
                    <td>
                      <input
                        type="number" min="0" max={component?.max_marks} step="0.5"
                        value={marksDraft[s.id] ?? ''}
                        onChange={(e) => setMarksDraft({ ...marksDraft, [s.id]: e.target.value })}
                        className="eop-score-input"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="eop-panel-footer">
              <button className="btn btn-primary" disabled={saving} onClick={saveMarks}>{saving ? 'Saving...' : 'Save Marks'}</button>
            </div>
          </div>
        )
      )}

      {showCreateComponent && (
        <div className="modal-overlay" onClick={() => setShowCreateComponent(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header"><h2>New Exam Component</h2><button className="btn-close" onClick={() => setShowCreateComponent(false)}>×</button></div>
            <form onSubmit={createComponent}>
              <div className="form-group"><label>Name *</label><input value={componentForm.name} onChange={(e) => setComponentForm({ ...componentForm, name: e.target.value })} placeholder="Paper 1, Practical, ..." required /></div>
              <div className="eop-picker-row">
                <div className="form-group"><label>Max Marks</label><input type="number" min="0" value={componentForm.max_marks} onChange={(e) => setComponentForm({ ...componentForm, max_marks: parseFloat(e.target.value) || 0 })} /></div>
                <div className="form-group"><label>Weight</label><input type="number" min="0" step="0.1" value={componentForm.weight} onChange={(e) => setComponentForm({ ...componentForm, weight: parseFloat(e.target.value) || 0 })} /></div>
              </div>
              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowCreateComponent(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary">Create</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------------------------------------------------------------- */
/* Result Publication                                                 */
/* ---------------------------------------------------------------- */

function PublicationTab({ sessions, setSessions }) {
  const [sessionId, setSessionId] = useState('');
  const [releaseDate, setReleaseDate] = useState('');
  const [saving, setSaving] = useState(false);

  const session = sessions.find((s) => s.id === sessionId);

  const pick = (id) => {
    setSessionId(id);
    const s = sessions.find((x) => x.id === id);
    setReleaseDate(s?.results_release_date || '');
  };

  const refresh = async () => {
    const data = await examsService.listSessions();
    setSessions(data || []);
  };

  const saveReleaseDate = async () => {
    setSaving(true);
    try {
      await examsService.setReleaseDate(sessionId, { results_release_date: releaseDate || null });
      toast.success('Release date saved');
      await refresh();
    } catch (error) {
      toast.error('Failed to save release date: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const publish = async () => {
    try {
      await examsService.publishResults(sessionId);
      toast.success('Results published');
      await refresh();
    } catch (error) {
      toast.error('Failed to publish: ' + error.message);
    }
  };

  const unpublish = async () => {
    try {
      await examsService.unpublishResults(sessionId);
      toast.success('Results unpublished');
      await refresh();
    } catch (error) {
      toast.error('Failed to unpublish: ' + error.message);
    }
  };

  return (
    <div className="eop-panel">
      <div className="form-group">
        <label>Exam Session</label>
        <select value={sessionId} onChange={(e) => pick(e.target.value)}>
          <option value="">Select session…</option>
          {sessions.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      </div>

      {session && (
        <div className="eop-card">
          <div className="ticket-info-grid">
            <div><strong>Status:</strong><StatusBadge status={session.results_published ? 'published' : 'pending'} /></div>
            <div><strong>Published By:</strong><span>{session.results_published_by || '—'}</span></div>
            <div><strong>Published At:</strong><span>{session.results_published_at ? new Date(session.results_published_at).toLocaleString() : '—'}</span></div>
          </div>

          <div className="form-group">
            <label>Scheduled Release Date (auto-publishes once this date passes)</label>
            <input type="date" value={releaseDate} onChange={(e) => setReleaseDate(e.target.value)} />
          </div>
          <div className="eop-row-actions">
            <button className="btn btn-secondary" disabled={saving} onClick={saveReleaseDate}>{saving ? 'Saving...' : 'Save Release Date'}</button>
            {session.results_published ? (
              <button className="btn btn-danger" onClick={unpublish}>Unpublish Now</button>
            ) : (
              <button className="btn btn-primary" onClick={publish}>Publish Now</button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------------------------------------------------------------- */
/* Remark Requests                                                    */
/* ---------------------------------------------------------------- */

function RemarksTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [reviewTarget, setReviewTarget] = useState(null);
  const [decision, setDecision] = useState('upheld');
  const [revisedScore, setRevisedScore] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await examOfficeService.listRemarkRequests(statusFilter ? { status_filter: statusFilter } : {});
      setItems(data || []);
    } catch (error) {
      toast.error('Failed to load remark requests: ' + error.message);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const openReview = (item) => {
    setReviewTarget(item);
    setDecision('upheld');
    setRevisedScore(String(item.original_score));
    setNotes('');
  };

  const submitReview = async () => {
    setSubmitting(true);
    try {
      await examOfficeService.reviewRemarkRequest(reviewTarget.id, {
        status: decision,
        revised_score: decision === 'revised' ? parseFloat(revisedScore) : null,
        review_notes: notes || null,
      });
      toast.success('Remark request reviewed');
      setReviewTarget(null);
      load();
    } catch (error) {
      toast.error('Failed to review request: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="eop-panel">
      <div className="filters">
        <select className="filter-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="">All Statuses</option>
          <option value="pending">Pending</option><option value="under_review">Under Review</option>
          <option value="upheld">Upheld</option><option value="revised">Revised</option><option value="rejected">Rejected</option>
        </select>
      </div>

      {loading ? (
        <div className="loading"><CircleNotch size={28} weight="bold" /> Loading...</div>
      ) : items.length === 0 ? (
        <div className="empty-state"><p>No remark requests found</p></div>
      ) : (
        <div className="eop-table">
          <table>
            <thead><tr><th>Student</th><th>Component</th><th>Reason</th><th>Original</th><th>Revised</th><th>Status</th><th>Actions</th></tr></thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id}>
                  <td>{item.student_name}</td>
                  <td>{item.component_name}</td>
                  <td className="eop-truncate">{item.reason}</td>
                  <td>{item.original_score}</td>
                  <td>{item.revised_score ?? '—'}</td>
                  <td><StatusBadge status={item.status} /></td>
                  <td>{['pending', 'under_review'].includes(item.status) && <button className="btn btn-sm btn-primary" onClick={() => openReview(item)}>Review</button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {reviewTarget && (
        <div className="modal-overlay" onClick={() => setReviewTarget(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header"><h2>Review Remark Request</h2><button className="btn-close" onClick={() => setReviewTarget(null)}>×</button></div>
            <p className="eop-hint">{reviewTarget.student_name} — {reviewTarget.component_name} — original score {reviewTarget.original_score}</p>
            <div className="form-group">
              <label>Decision</label>
              <select value={decision} onChange={(e) => setDecision(e.target.value)}>
                <option value="under_review">Mark Under Review</option>
                <option value="upheld">Uphold (no change)</option>
                <option value="revised">Revise Score</option>
                <option value="rejected">Reject</option>
              </select>
            </div>
            {decision === 'revised' && (
              <div className="form-group"><label>Revised Score</label><input type="number" step="0.5" value={revisedScore} onChange={(e) => setRevisedScore(e.target.value)} /></div>
            )}
            <div className="form-group"><label>Notes</label><textarea rows="2" value={notes} onChange={(e) => setNotes(e.target.value)} /></div>
            <div className="form-actions">
              <button className="btn btn-secondary" onClick={() => setReviewTarget(null)}>Cancel</button>
              <button className="btn btn-primary" disabled={submitting} onClick={submitReview}>{submitting ? 'Saving...' : 'Confirm'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------------------------------------------------------------- */
/* Malpractice Cases                                                  */
/* ---------------------------------------------------------------- */

function MalpracticeTab({ sessions }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const picker = useScheduleOptions(sessions);
  const [students, setStudents] = useState([]);
  const [components, setComponents] = useState([]);
  const [form, setForm] = useState({ student_id: '', exam_component_id: '', category: 'other', description: '' });
  const [submitting, setSubmitting] = useState(false);
  const [detail, setDetail] = useState(null);
  const [updateForm, setUpdateForm] = useState({ status: '', investigation_notes: '', sanction: '', sanction_notes: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await examOfficeService.listMalpracticeCases(statusFilter ? { status_filter: statusFilter } : {});
      setItems(data || []);
    } catch (error) {
      toast.error('Failed to load malpractice cases: ' + error.message);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!picker.scheduleId) { setStudents([]); setComponents([]); return; }
    const schedule = picker.schedules.find((s) => s.id === picker.scheduleId);
    if (schedule) {
      classesService.getClassStudents(schedule.class_id).then((r) => setStudents(r.items || r || [])).catch(() => {});
    }
    examOfficeService.listComponents(picker.scheduleId).then((c) => setComponents(c || [])).catch(() => {});
  }, [picker.scheduleId, picker.schedules]);

  const submitCreate = async (e) => {
    e.preventDefault();
    if (!picker.scheduleId || !form.student_id || !form.description.trim()) {
      toast.error('Schedule, student and description are required');
      return;
    }
    setSubmitting(true);
    try {
      await examOfficeService.createMalpracticeCase({ ...form, exam_schedule_id: picker.scheduleId, exam_component_id: form.exam_component_id || null });
      toast.success('Case recorded');
      setShowCreate(false);
      setForm({ student_id: '', exam_component_id: '', category: 'other', description: '' });
      load();
    } catch (error) {
      toast.error('Failed to record case: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const openDetail = (item) => {
    setDetail(item);
    setUpdateForm({ status: item.status, investigation_notes: item.investigation_notes || '', sanction: item.sanction || 'none', sanction_notes: item.sanction_notes || '' });
  };

  const saveUpdate = async () => {
    try {
      await examOfficeService.updateMalpracticeCase(detail.id, updateForm);
      toast.success('Case updated');
      setDetail(null);
      load();
    } catch (error) {
      toast.error('Failed to update case: ' + error.message);
    }
  };

  return (
    <div className="eop-panel">
      <div className="filters">
        <select className="filter-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="">All Statuses</option>
          <option value="reported">Reported</option><option value="under_investigation">Under Investigation</option>
          <option value="resolved">Resolved</option><option value="dismissed">Dismissed</option>
        </select>
        <button className="btn btn-primary" onClick={() => setShowCreate(true)}>Report Case</button>
      </div>

      {loading ? (
        <div className="loading"><CircleNotch size={28} weight="bold" /> Loading...</div>
      ) : items.length === 0 ? (
        <div className="empty-state"><p>No malpractice cases recorded</p></div>
      ) : (
        <div className="eop-table">
          <table>
            <thead><tr><th>Student</th><th>Category</th><th>Status</th><th>Sanction</th><th>Actions</th></tr></thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id}>
                  <td>{item.student_name}</td>
                  <td>{item.category.replace(/_/g, ' ')}</td>
                  <td><StatusBadge status={item.status} /></td>
                  <td>{item.sanction ? item.sanction.replace(/_/g, ' ') : '—'}</td>
                  <td><button className="btn btn-sm btn-secondary" onClick={() => openDetail(item)}>Manage</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showCreate && (
        <div className="modal-overlay" onClick={() => setShowCreate(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header"><h2>Report Malpractice Case</h2><button className="btn-close" onClick={() => setShowCreate(false)}>×</button></div>
            <form onSubmit={submitCreate}>
              <SessionScheduleSelect sessions={sessions} picker={picker} subjectName={(id) => id} />
              <div className="form-group">
                <label>Student *</label>
                <select value={form.student_id} onChange={(e) => setForm({ ...form, student_id: e.target.value })} required disabled={!students.length}>
                  <option value="">Select student…</option>
                  {students.map((s) => <option key={s.id} value={s.id}>{s.full_name || `${s.first_name} ${s.last_name}`}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Component (optional)</label>
                <select value={form.exam_component_id} onChange={(e) => setForm({ ...form, exam_component_id: e.target.value })}>
                  <option value="">— None —</option>
                  {components.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Category</label>
                <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                  <option value="unauthorized_material">Unauthorized Material</option>
                  <option value="collusion">Collusion</option>
                  <option value="impersonation">Impersonation</option>
                  <option value="plagiarism">Plagiarism</option>
                  <option value="disruptive_conduct">Disruptive Conduct</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div className="form-group"><label>Description *</label><textarea rows="3" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} required /></div>
              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowCreate(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={submitting}>{submitting ? 'Saving...' : 'Report Case'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {detail && (
        <div className="modal-overlay" onClick={() => setDetail(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header"><h2>{detail.student_name} — {detail.category.replace(/_/g, ' ')}</h2><button className="btn-close" onClick={() => setDetail(null)}>×</button></div>
            <p className="eop-hint">{detail.description}</p>
            <div className="form-group">
              <label>Status</label>
              <select value={updateForm.status} onChange={(e) => setUpdateForm({ ...updateForm, status: e.target.value })}>
                <option value="reported">Reported</option><option value="under_investigation">Under Investigation</option>
                <option value="resolved">Resolved</option><option value="dismissed">Dismissed</option>
              </select>
            </div>
            <div className="form-group"><label>Investigation Notes</label><textarea rows="2" value={updateForm.investigation_notes} onChange={(e) => setUpdateForm({ ...updateForm, investigation_notes: e.target.value })} /></div>
            <div className="form-group">
              <label>Sanction</label>
              <select value={updateForm.sanction} onChange={(e) => setUpdateForm({ ...updateForm, sanction: e.target.value })}>
                <option value="none">None</option><option value="warning">Warning</option>
                <option value="result_annulled">Result Annulled</option><option value="suspension">Suspension</option>
                <option value="expulsion">Expulsion</option><option value="other">Other</option>
              </select>
            </div>
            {updateForm.sanction === 'result_annulled' && !detail.exam_component_id && (
              <p className="eop-hint eop-warn">This case has no linked component — annulment won't be able to flag a specific mark.</p>
            )}
            <div className="form-group"><label>Sanction Notes</label><textarea rows="2" value={updateForm.sanction_notes} onChange={(e) => setUpdateForm({ ...updateForm, sanction_notes: e.target.value })} /></div>
            <div className="form-actions">
              <button className="btn btn-secondary" onClick={() => setDetail(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={saveUpdate}>Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ExamOfficePage;
